import HBEmpty from "@/components/HBEmpty";
import HBLoading from "@/components/HBLoading";
import { useLoadingLimit } from "@/components/LoginModal/useLoginLimit";
import PinList from "@/components/PinList";
import useAppStore from "@/context/store";
import { getTopicPins } from "@/services";
import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import { useMemoizedFn } from "ahooks";
import { Button } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import styles from "./index.module.less";

function TopicEmpty() {
  return (
    <HBEmpty
      className={styles.empty}
      size="small"
      image="//cdn.dancf.com/fe-assets/img/6a5695b2efbda504b26a212d160553ec.svg"
      title="还没有采集哦~"
    />
  );
}

export function TopicLimitPinList({ values, defaultPins, showDownload }) {
  const [dataSource, setDataSource] = useState(defaultPins || []);
  const [loading, setLoading] = useState(defaultPins?.length <= 0);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(defaultPins?.length > 0 ? 2 : 1);
  const initRef = useRef(defaultPins?.length > 0);
  const user = useAppStore((s) => s.user);

  const queryAction = useMemo(() => {
    if (initRef.current) {
      if (values) {
        initRef.current = false;
      }
    } else {
      pageRef.current = 1;
      setDataSource([]);
      setLoading(true);
      setHasMore(true);
    }

    return async () => {
      if (!values) {
        return;
      }

      setLoading(true);
      const result = await getTopicPins({
        per_page: 80,
        page: pageRef.current,
        filter_ids: values?.ids || [],
        sort: values?.sort || "all",
        position: "topics",
      });

      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource((dataSource) => dataSource.concat(pins));
      setLoading(false);
    };
  }, [values]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const { onLoadLimitCheck } = useLoadingLimit({
    pageListData: dataSource,
  });

  const onLoadMore = useMemoizedFn(async () => {
    if (dataSource.length > 0 && !initRef.current) {
      const res = await onLoadLimitCheck();

      if (res) {
        pageRef.current++;
        queryAction();
        pvTracker(getPageData());
      }
    }
  });

  return (
    <div
      className={styles.list}
      data-sort-type={values?.sortName}
      data-filter-one={values?.filterOne}
    >
      <If condition={dataSource.length > 0}>
        <Then>
          <PinList
            dataSource={dataSource}
            hasMore={false}
            showDownload={showDownload}
            endMessage={
              <If condition={hasMore}>
                <Then>
                  <If condition={loading}>
                    <Then>
                      <div className={styles.loading}>
                        <HBLoading />
                      </div>
                    </Then>
                    <Else>
                      <div className={styles.end}>
                        <Button
                          className={styles.button}
                          type="text"
                          size="large"
                          onClick={onLoadMore}
                        >
                          展开更多
                        </Button>
                      </div>
                    </Else>
                  </If>
                </Then>
                <Else>
                  <div
                    data-gd-expose="resource_loc_expose"
                    data-resource-title="没有更多了"
                    data-resource-type="文字"
                    className={styles.noMore}
                  >
                    没有更多了
                  </div>
                </Else>
              </If>
            }
          />
        </Then>
        <Else>
          <If condition={loading}>
            <Then>
              <div className={styles.largeLoading}>
                <HBLoading />
              </div>
            </Then>
            <Else>
              <TopicEmpty />
            </Else>
          </If>
        </Else>
      </If>
    </div>
  );
}

export default function TopicPinList({ values, defaultPins, showDownload }) {
  const [dataSource, setDataSource] = useState(defaultPins || []);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(defaultPins?.length <= 0);
  const pageRef = useRef(defaultPins?.length > 0 ? 2 : 1);
  const initRef = useRef(defaultPins?.length > 0);

  const queryAction = useMemo(() => {
    if (initRef.current) {
      if (values) {
        initRef.current = false;
      }
    } else {
      pageRef.current = 1;
      setDataSource([]);
      setHasMore(true);
    }

    return async () => {
      if (!values) {
        return;
      }

      setLoading(true);
      const result = await getTopicPins({
        per_page: 40,
        page: pageRef.current,
        filter_ids: values?.ids || [],
        sort: values?.sort || "all",
        position: "topics",
      });

      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource((dataSource) => dataSource.concat(pins));
      setLoading(false);
    };
  }, [values]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const onLoadMore = useCallback(() => {
    if (dataSource.length > 0 && !initRef.current) {
      pageRef.current++;
      queryAction();

      pvTracker(getPageData());
    }
  }, [dataSource, queryAction]);

  return (
    <div
      className={styles.list}
      data-sort-type={values?.sortName}
      data-filter-one={values?.filterOne}
    >
      <If condition={dataSource.length > 0}>
        <Then>
          <PinList
            dataSource={dataSource}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            showDownload={showDownload}
          />
        </Then>
        <Else>
          <If condition={loading}>
            <Then>
              <div className={styles.loading}>
                <HBLoading />
              </div>
            </Then>
            <Else>
              <TopicEmpty />
            </Else>
          </If>
        </Else>
      </If>
    </div>
  );
}
